import {
  Box,
  Card,
  Stack,
  TextField as MuiTextField,
  MenuItem,
  Grid,
  Button,
  TablePagination,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { Datagrid, TextField, FunctionField, useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import {
  getDailyPrizePoolConfigAuditHistory,
  DailyPrizePoolConfigAudit,
  DailyPrizePoolConfigAuditFilter,
  PoolType,
} from '../../../services/redpacketService';

const DailyPrizePoolConfigAuditList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [data, setData] = useState<DailyPrizePoolConfigAudit[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 0, perPage: 20 });
  const [filter, setFilter] = useState<DailyPrizePoolConfigAuditFilter>({});
  const [filterValues, setFilterValues] = useState<{ poolType: PoolType | '' }>({ poolType: '' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const offset = pagination.page * pagination.perPage;
      const result = await getDailyPrizePoolConfigAuditHistory(filter, {
        limit: pagination.perPage,
        offset,
      });
      setData(result.records ?? []);
      setTotal(result.totalCount ?? 0);
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.failedToGetData'), { type: 'error' });
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, filter, notify,translate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilter = () => {
    const newFilter: DailyPrizePoolConfigAuditFilter = {
      poolType: filterValues.poolType || undefined,
    };
    setFilter(newFilter);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPagination((prev) => ({ ...prev, perPage: parseInt(e.target.value, 10), page: 0 }));
  };

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.dailyPrizePoolConfigAudit')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.dailyPrizePoolConfigAudit') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <MuiTextField
                  fullWidth
                  select
                  label={translate('module.redpacket.poolType')}
                  value={filterValues.poolType}
                  onChange={(e) => handleFilterChange('poolType', e.target.value)}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={PoolType.SHARED}>SHARED</MenuItem>
                  <MenuItem value={PoolType.CRITICAL_HIT}>CRITICAL_HIT</MenuItem>
                  <MenuItem value={PoolType.CRITICAL_HIT_DESIGNATED}>CRITICAL_HIT_DESIGNATED</MenuItem>
                </MuiTextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack direction="row" spacing={2}>
                  <Button variant="contained" onClick={handleApplyFilter}>
                    {translate('module.redpacket.search')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setFilterValues({ poolType: '' });
                      setFilter({});
                    }}
                  >
                    {translate('module.redpacket.reset')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ overflow: 'auto' }}>
            <Datagrid
              rowClick={false}
              data={data}
              isLoading={loading}
              total={total}
              bulkActionButtons={false}
            >
              <TextField source="id" label="ID" sortable={false} />
              <TextField source="poolType" label={translate('module.redpacket.poolType')} />
              <FunctionField
                label={translate('module.redpacket.oldBudget')}
                render={(record: DailyPrizePoolConfigAudit) => record.oldBudget ?? '-'}
              />
              <TextField source="newBudget" label={translate('module.redpacket.newBudget')} />
              <FunctionField
                label={translate('module.redpacket.changedBy')}
                render={(record: DailyPrizePoolConfigAudit) => record.changedBy ?? '-'}
              />
              <FunctionField
                label={translate('module.redpacket.changeReason')}
                render={(record: DailyPrizePoolConfigAudit) => record.changeReason ?? '-'}
              />
              <FunctionField
                label={translate('module.redpacket.createdAtCol')}
                render={(record: DailyPrizePoolConfigAudit) =>
                  record.createdAt ? new Date(record.createdAt).toLocaleString() : '-'
                }
              />
            </Datagrid>
          </Box>
          <TablePagination
            component="div"
            count={total}
            page={pagination.page}
            onPageChange={handlePageChange}
            rowsPerPage={pagination.perPage}
            onRowsPerPageChange={handlePerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default DailyPrizePoolConfigAuditList;
